import Form from '@components/Form'
import { Controller, type UseFormReturn } from 'react-hook-form'
import { Icon } from 'shared-ui/components'
import { SpriteMap } from 'shared-ui/global'
import { useLocalization } from 'shared-ui/localization'
import { convertFromImperial, convertToImperial } from 'utils'

import type { FormValues } from './formtypes'

export const Details = ({ form }: { form: UseFormReturn<FormValues> }) => {
  const { control, register, watch, setValue, getValues } = form
  const { t } = useLocalization()

  return (
    <div className="flex flex-col gap-4 py-4">
      <p className="text-sm text-gray-500">{t(($) => $.planning.guidanceGettingStarted)}</p>

      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium text-gray-700">
          {t(($) => $.planning.guidanceName)}
        </span>
        <Form.Input {...register('name')} placeholder={t(($) => $.labels.name)} />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium text-gray-700">
          {t(($) => $.planning.implementWidth)}
        </span>
        <div className="flex items-start gap-2">
          <Form.Input
            {...register('implementWidth', { valueAsNumber: true })}
            type="number"
            min={1}
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm outline-green-500"
          />
          <Controller
            control={control}
            name="implementWidthUnit"
            render={({ field }) => (
              <div className="relative">
                <select
                  value={field.value}
                  onChange={(e) => {
                    const newUnit = e.target.value as 'ft' | 'm'
                    const current = getValues('implementWidth')
                    const converted =
                      field.value === 'ft'
                        ? convertFromImperial(current, 'ft')
                        : convertToImperial(current, 'm')
                    setValue('implementWidth', Math.round(converted * 10) / 10)
                    field.onChange(newUnit)
                  }}
                  className="w-20 appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-7 text-sm outline-green-500"
                >
                  <option value="ft">ft</option>
                  <option value="m">m</option>
                </select>
                <Icon
                  icon={SpriteMap.ChevronIcon}
                  className="pointer-events-none absolute right-2 top-1/2 size-3 -translate-y-1/2 rotate-180 text-gray-500"
                />
              </div>
            )}
          />
        </div>
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium text-gray-700">{t(($) => $.planning.rowSpacing)}</span>
        <div className="flex items-start gap-2">
          <Form.Input
            {...register('rowSpacing', { valueAsNumber: true })}
            type="number"
            min={1}
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm outline-green-500"
          />
          <Controller
            control={control}
            name="rowSpacingUnit"
            render={({ field }) => (
              <div className="relative">
                <select
                  value={field.value}
                  onChange={(e) => {
                    const newUnit = e.target.value as 'in' | 'cm'
                    const current = getValues('rowSpacing')
                    const converted =
                      field.value === 'in'
                        ? convertFromImperial(current, 'in')
                        : convertToImperial(current, 'cm')
                    setValue('rowSpacing', Math.round(converted * 10) / 10)
                    field.onChange(newUnit)
                  }}
                  className="w-20 appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-7 text-sm outline-green-500"
                >
                  <option value="in">in</option>
                  <option value="cm">cm</option>
                </select>
                <Icon
                  icon={SpriteMap.ChevronIcon}
                  className="pointer-events-none absolute right-2 top-1/2 size-3 -translate-y-1/2 rotate-180 text-gray-500"
                />
              </div>
            )}
          />
        </div>
      </label>

      <div className="flex gap-2">
        <label className="flex w-full flex-col gap-1">
          <span className="text-sm font-medium text-gray-700">
            {t(($) => $.planning.numberOfHeadlands)}
          </span>
          <Form.Input
            className="w-full"
            {...register('numberOfHeadlands', { valueAsNumber: true })}
            type="number"
            min={0}
          />
        </label>

        <label className="flex w-full flex-col gap-1">
          <span className="text-sm font-medium text-gray-700">
            {t(($) => $.planning.totalWidth)}
          </span>
          <Form.Input
            className="w-full"
            disabled
            readOnly
            type="text"
            value={`${Math.round(watch('numberOfHeadlands') * watch('implementWidth') * 10) / 10} ${watch('implementWidthUnit')}`}
          />
        </label>
      </div>
    </div>
  )
}
